import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase, isSupabaseConfigured, landedOnRecoveryLink } from "../lib/supabase.js";
import { uploadAvatar, deletePhoto, signPaths, validateImage } from "../lib/storage.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  // Whatever the profile photo is renderable from right now — a public avatar
  // URL, or a signed link to an older one in the private bucket.
  const [avatarUrl, setAvatarUrl] = useState(null);
  // `loading` covers the initial session restore only. Until it clears we must
  // not render either the app or the login screen, or a returning user sees a
  // flash of the login screen before their session comes back.
  const [loading, setLoading] = useState(isSupabaseConfigured);
  /* Arrived by clicking "reset my password". The seed comes from the URL,
     which is read in lib/supabase.js before the client can scrub it; the
     PASSWORD_RECOVERY event below sets it too, for the case where the app was
     already open. Either way it means: there is a session, but the only thing
     it is good for is setting a new password. */
  const [recovering, setRecovering] = useState(landedOnRecoveryLink);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session ?? null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next ?? null);
      setLoading(false);
      /* A reset link creates a real session, so without reading the event the app
         simply lets someone in — signed in, with no way to set the password they
         came here to set. The event is the reliable half when the app is already
         open; the URL check in lib/supabase.js is the half that survives a cold
         load, where this listener may not exist yet. */
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user?.id ?? null;

  // Profile is best-effort: the app is fully usable without it, so a failure
  // here degrades the greeting rather than blocking the collection.
  useEffect(() => {
    if (!isSupabaseConfigured || !userId) {
      setProfile(null);
      return;
    }
    let active = true;

    (async () => {
      const { data } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
      if (!active) return;
      if (data) {
        setProfile(data);
        return;
      }

      /* No profile row. Either the signup trigger could not be installed (it
         needs ownership of auth.users, which not every project grants), or this
         account predates it. Create it here so the app never depends on that
         trigger existing. Legal acceptance was recorded in the user's signup
         metadata, which is authoritative either way — mirror it across. */
      const meta = session?.user?.user_metadata ?? {};
      const now = new Date().toISOString();
      const { data: created } = await supabase
        .from("profiles")
        .upsert(
          {
            id: userId,
            display_name: meta.display_name || meta.full_name || session?.user?.email?.split("@")[0] || null,
            accepted_terms_at: meta.accepted_terms ? now : null,
            accepted_privacy_at: meta.accepted_privacy ? now : null,
            accepted_beta_at: meta.accepted_beta ? now : null,
          },
          { onConflict: "id", ignoreDuplicates: true }
        )
        .select()
        .maybeSingle();

      if (active && created) setProfile(created);
    })();

    return () => {
      active = false;
    };
    // `session` is intentionally not a dependency: it changes identity on every
    // token refresh, and re-running this on refresh would be pointless work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /* Two generations of avatar live here. New ones go to the public `avatars`
     bucket and are stored as a finished URL, so other collectors can see them
     in Baggage Claim. Ones taken before that change are objects in the private
     bucket, readable only by their owner and only through a signed link.
     avatar_url wins when both exist; the path is the fallback, which is what
     stops a collector's face disappearing on the day this ships. */
  useEffect(() => {
    let active = true;
    const url = profile?.avatar_url;
    if (url) {
      setAvatarUrl(url);
      return;
    }
    const path = profile?.avatar_path;
    if (!path) {
      setAvatarUrl(null);
      return;
    }
    signPaths([path]).then((map) => {
      if (active) setAvatarUrl(map[path] || null);
    });
    return () => {
      active = false;
    };
  }, [profile?.avatar_url, profile?.avatar_path]);

  /* A signed viewing link expires an hour after it is made, so a session left
     open past that would show a broken circle where the photo is. The picture
     itself is untouched — only the link needs replacing. Public avatar URLs do
     not expire, so there is nothing to refresh for those. */
  const refreshAvatar = useCallback(async () => {
    if (profile?.avatar_url) return;
    const path = profile?.avatar_path;
    if (!path) return;
    const map = await signPaths([path]);
    if (map[path]) setAvatarUrl(map[path]);
  }, [profile?.avatar_url, profile?.avatar_path]);

  /* Called once the new password is saved — or if the collector backs out. It
     only lowers the flag; the session itself is a perfectly ordinary one and
     stays, so they land in their Passport already signed in. */
  const endRecovery = useCallback(() => setRecovering(false), []);

  const setAvatar = useMemo(
    () => async (file) => {
      if (!userId || !file) return { error: null };
      const problem = validateImage(file);
      if (problem) return { error: problem };
      try {
        const previous = profile?.avatar_path;
        const url = await uploadAvatar(userId, file);
        /* avatar_path is cleared in the same write: leaving it set would keep a
           private copy nobody reads, and the old photo is deleted below. */
        const { data, error } = await supabase
          .from("profiles")
          .update({ avatar_url: url, avatar_path: null })
          .eq("id", userId)
          .select()
          .single();
        if (error) throw error;
        setProfile(data);
        // Best effort — a stale avatar left in storage costs nothing.
        if (previous) deletePhoto(previous).catch(() => {});
        return { error: null };
      } catch {
        return { error: "Couldn't upload that photo." };
      }
    },
    [userId, profile?.avatar_path]
  );

  const value = useMemo(() => {
    const displayName =
      profile?.display_name ||
      session?.user?.user_metadata?.display_name ||
      session?.user?.user_metadata?.full_name ||
      session?.user?.email?.split("@")[0] ||
      "Collector";

    return {
      session,
      user: session?.user ?? null,
      userId,
      profile,
      displayName,
      avatarUrl,
      setAvatar,
      refreshAvatar,
      loading,
      recovering,
      endRecovery,
      signOut: async () => {
        if (!supabase) return;
        await supabase.auth.signOut();
        setProfile(null);
        // Otherwise signing out from the reset screen and back in with the old
        // password would drop you straight back onto it.
        setRecovering(false);
      },
      refreshProfile: async () => {
        if (!supabase || !userId) return;
        const { data } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
        setProfile(data ?? null);
      },
    };
  }, [session, userId, profile, loading, avatarUrl, setAvatar, refreshAvatar, recovering, endRecovery]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Provider and hook stay together deliberately: splitting a context into two
// files to satisfy Fast Refresh costs more than the hot-reload it buys.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
